"use client";

import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { Footer } from "@/components/sections/premium/ManifestoSection";

const TOTAL = 16;
const PDF_URL = "/pitch/dotslashlab-pitch-deck.pdf";
const slideSrc = (i: number) => `/pitch/slides/slide-${String(i + 1).padStart(2, "0")}.jpg`;
const slides = Array.from({ length: TOTAL }, (_, i) => slideSrc(i));

export const PitchDeckContent = () => {
  const [active, setActive] = useState<number | null>(null);

  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(() => setActive((i) => (i === null ? i : (i - 1 + TOTAL) % TOTAL)), []);
  const next = useCallback(() => setActive((i) => (i === null ? i : (i + 1) % TOTAL)), []);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prevOverflow; };
  }, [active, close, prev, next]);

  return (
    <main className="relative bg-[#0a0a0a] text-[#f4f4f0] min-h-screen selection:bg-primary selection:text-white font-sans overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-[80vh] w-full flex flex-col justify-end p-6 md:p-12 overflow-hidden bg-black">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(255,255,255,0.06),_transparent_55%)]" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a0a0a]" />
        </div>

        <div className="relative z-10 flex justify-between items-start mt-24 mb-auto">
          <motion.span
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.3 }}
            className="font-sans text-sm md:text-base uppercase tracking-[0.4em] text-white/50"
          >
            DOTSLASHLAB / INVESTOR DECK
          </motion.span>
          <motion.span
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.3 }}
            className="font-mono text-xs md:text-sm text-white/30 text-right tracking-widest"
          >
            MUMBAI · DUBAI <br /> {TOTAL} SLIDES
          </motion.span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10"
        >
          <h1 className="font-display text-[16vw] md:text-[13vw] leading-[0.8] tracking-tight uppercase">
            PITCH <span className="text-transparent italic" style={{ WebkitTextStroke: "2px #f4f4f0" }}>DECK.</span>
          </h1>
          <div className="mt-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <p className="font-sans text-lg md:text-2xl text-white/60 max-w-2xl leading-relaxed">
              AI, blockchain and full-stack products for startups, enterprises and governments. The studio, the numbers, and the work.
            </p>
            <a
              href={PDF_URL}
              download
              className="shrink-0 inline-flex items-center gap-3 bg-[#f4f4f0] text-[#0a0a0a] hover:bg-primary hover:text-white px-8 py-4 rounded-full font-mono text-[10px] uppercase tracking-[0.3em] transition-colors duration-500"
            >
              <Download className="w-4 h-4" /> Download deck (PDF)
            </a>
          </div>
        </motion.div>
      </section>

      {/* SLIDES */}
      <section className="relative z-10 bg-[#0a0a0a] px-4 md:px-12 py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto space-y-6 md:space-y-10">
          {slides.map((src, i) => (
            <motion.button
              key={src}
              type="button"
              onClick={() => setActive(i)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.6 }}
              className="group relative block w-full overflow-hidden rounded-xl md:rounded-2xl border border-white/10 bg-black focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={src}
                alt={`DotSlashLab pitch deck — slide ${i + 1} of ${TOTAL}`}
                width={1600}
                height={900}
                loading={i < 2 ? "eager" : "lazy"}
                className="w-full h-auto block aspect-video object-cover transition-transform duration-700 group-hover:scale-[1.015]"
              />
              <span className="absolute top-3 left-3 md:top-5 md:left-5 font-mono text-[10px] md:text-xs tracking-[0.3em] text-white/60 bg-black/40 backdrop-blur px-3 py-1.5 rounded-full">
                {String(i + 1).padStart(2, "0")} / {TOTAL}
              </span>
              <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-black/20">
                <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white bg-black/60 px-5 py-3 rounded-full">
                  <Maximize2 className="w-3.5 h-3.5" /> View
                </span>
              </span>
            </motion.button>
          ))}
        </div>
      </section>

      <Footer />

      {/* LIGHTBOX */}
      {active !== null && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 md:p-10"
          onClick={close}
        >
          <button onClick={close} aria-label="Close" className="absolute top-5 right-5 z-10 p-3 text-white/70 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous slide"
            className="absolute left-2 md:left-6 z-10 p-3 text-white/60 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-8 h-8 md:w-10 md:h-10" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next slide"
            className="absolute right-2 md:right-6 z-10 p-3 text-white/60 hover:text-white transition-colors"
          >
            <ChevronRight className="w-8 h-8 md:w-10 md:h-10" />
          </button>

          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={slides[active]}
            alt={`DotSlashLab pitch deck — slide ${active + 1} of ${TOTAL}`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[92vw] w-auto h-auto object-contain rounded-lg border border-white/10 shadow-2xl"
          />
          <span className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-xs tracking-[0.3em] text-white/60">
            {String(active + 1).padStart(2, "0")} / {TOTAL}
          </span>
        </div>
      )}

      <div className="bg-noise opacity-10 pointer-events-none fixed inset-0 z-50 mix-blend-overlay" />
    </main>
  );
};
